import { useTemporaryToken } from '@netfront/gelada-identity-library';
import { getAccessTokenCookie, isSecureCookie, setCookie, stripBearerFromToken } from '@netfront/common-library';
import { useEffect, useState } from 'react';
import { environmentVariables } from '@/utils/environmentVariables';

type UseGetTemporarySessionReturn = {
  token: string | null;
  isLoading: boolean;
};

const SESSION_DURATION_MS = 60 * 60 * 1000; // matches getTemporarySession(duration: 60)

function setTemporarySessionCookies(token: string) {
  const value = stripBearerFromToken(token);
  const expires = new Date(Date.now() + SESSION_DURATION_MS).toUTCString();

  // Match the same SameSite/Secure attributes the Apollo link uses when
  // persisting real auth tokens — SameSite=None requires Secure on HTTPS only.
  const secure = isSecureCookie(process.env.REACT_APP_COOKIE_ATTRIBUTE_SECURE);
  const sameSite = secure ? ('None' as const) : ('Lax' as const);
  const base = { value, path: '/', sameSite, ...(secure ? { secure } : {}), expires };

  // Apollo link bails without Authorization if either accessToken OR refreshToken
  // cookie is missing (!N || !P check). Set both to the same temporary token.
  setCookie({ name: 'accessToken', ...base });
  setCookie({ name: 'refreshToken', ...base });
}

export default function useGetTemporarySession(): UseGetTemporarySessionReturn {
  const [token, setToken] = useState<string | null>(() => getAccessTokenCookie() ?? null);

  const { handleGetTemporaryToken, isLoading } = useTemporaryToken({
    projectId: environmentVariables.projectId,
    onCompleted: ({ temporaryToken }) => {
      setTemporarySessionCookies(temporaryToken);
      setToken(temporaryToken);
    },
  });

  useEffect(() => {
    if (!getAccessTokenCookie()) {
      // Intentionally not awaited: result is handled via the onCompleted callback above. NOSONAR
      void handleGetTemporaryToken();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { token, isLoading: isLoading ?? false };
}
